import React from 'react';
import { View } from '@react-pdf/renderer';
import { computeBoxPlotDomain } from '../../../util/RangeCalculation';
import { BAND_RED, BAND_YELLOW, BAND_GREEN } from '../../../util/Colors';

/**
 * PDF-compatible BoxPlot with banded color zones matching the web BoxPlot.
 *
 * Layout (left to right):
 *   [red zone] [yellow zone] [green zone] [yellow zone] [red zone]
 *
 * Result marker: vertical dark line with rounded ends.
 */
function PDFBoxPlot({ llow, lhigh, fxlow, fxhigh, result, width = 140, height = 28 }) {
    const domain = computeBoxPlotDomain(result, llow, lhigh, fxlow, fxhigh);
    if (!domain) return null;
    const { result: resultVal, llow: labLow, lhigh: labHigh, fxL, fxH, displayMin, displayMax } = domain;
    const span = Math.max(1e-9, displayMax - displayMin);

    const getPct = (v) => ((v - displayMin) / span) * 100;

    const labLowPct = getPct(labLow);
    const labHighPct = getPct(labHigh);
    const fxLowPct = getPct(fxL);
    const fxHighPct = getPct(fxH);
    const resultPct = getPct(resultVal);

    const red = BAND_RED;
    const yellow = BAND_YELLOW;
    const green = BAND_GREEN;

    const borderRadius = 4;
    const lineWidth = 2.5;
    const lineHeight = height * 0.85;
    const lineTop = (height - lineHeight) / 2;

    return (
        <View style={{
            width,
            height,
            position: 'relative',
            flexDirection: 'row',
            borderRadius,
            overflow: 'hidden',
        }}>
            {/* Red band: left edge → lab low */}
            <View style={{
                position: 'absolute',
                left: 0,
                width: `${labLowPct}%`,
                top: 0,
                height,
                backgroundColor: red,
            }} />

            {/* Yellow band: lab low → fx low */}
            <View style={{
                position: 'absolute',
                left: `${labLowPct}%`,
                width: `${fxLowPct - labLowPct}%`,
                top: 0,
                height,
                backgroundColor: yellow,
            }} />

            {/* Green band: fx low → fx high */}
            <View style={{
                position: 'absolute',
                left: `${fxLowPct}%`,
                width: `${fxHighPct - fxLowPct}%`,
                top: 0,
                height,
                backgroundColor: green,
            }} />

            {/* Yellow band: fx high → lab high */}
            <View style={{
                position: 'absolute',
                left: `${fxHighPct}%`,
                width: `${labHighPct - fxHighPct}%`,
                top: 0,
                height,
                backgroundColor: yellow,
            }} />

            {/* Red band: lab high → right edge */}
            <View style={{
                position: 'absolute',
                left: `${labHighPct}%`,
                width: `${100 - labHighPct}%`,
                top: 0,
                height,
                backgroundColor: red,
            }} />

            {/* Result marker: vertical line */}
            <View style={{
                position: 'absolute',
                left: `${resultPct}%`,
                marginLeft: -lineWidth / 2,
                top: lineTop,
                width: lineWidth,
                height: lineHeight,
                backgroundColor: '#1f2937',
                borderRadius: lineWidth / 2,
            }} />
        </View>
    );
}

export default PDFBoxPlot;
